import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import {
  FieldArrayType,
  FieldArrayTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TabsModule } from 'primeng/tabs';

interface FormlyTabsRepeatProps extends FormlyFieldProps {
  labelField?: string;
  addButton: boolean;
  newItem: (currentItem?: any) => any;
}

export interface FormlyTabsRepeatFieldConfig
  extends FieldArrayTypeConfig<FormlyTabsRepeatProps> {}

@Component({
  selector: 'kon-dynamic-form-tabs-array',
  imports: [
    CommonModule,
    TabsModule,
    FormlyModule,
    ButtonModule,
    ConfirmPopupModule,
  ],
  template: `
    <p-tabs [ngClass]="field.className" [(value)]="activeIndex">
      <p-tablist>
        @for (tab of field.fieldGroup; track tab; let i = $index) {
          <p-tab [value]="i">
            {{
              props.labelField && model[i]
                ? (model[i][props.labelField] ?? '#' + i)
                : '#' + i
            }}
            @if (!options.formState.readonly) {
              <p-confirmpopup [key]="'tab-remove-' + i" />
              <p-button
                type="button"
                icon="pi pi-times"
                class="ml-2"
                [rounded]="true"
                [text]="true"
                severity="secondary"
                (click)="tabRemove(i, $event)"
              />
            }
          </p-tab>
        }
        @if (props.addButton) {
          <p-button
            icon="pi pi-plus"
            [rounded]="true"
            [text]="true"
            (click)="tabAdd()"
            [disabled]="options.formState.readonly"
            class="content-center"
          />
        }
      </p-tablist>
      <p-tabpanels>
        @for (tab of field.fieldGroup; track tab; let i = $index) {
          <p-tabpanel [ngClass]="field.fieldGroupClassName" [value]="i">
            <formly-field [field]="tab"></formly-field>
          </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
  `,
  styles: [``],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class FormlyTabsRepeatTypeComponent
  extends FieldArrayType<FieldArrayTypeConfig<FormlyTabsRepeatProps>>
  implements OnInit
{
  confirmationService = inject(ConfirmationService);
  cdr = inject(ChangeDetectorRef);

  override defaultOptions = {
    props: {
      newItem: (current: any) => current ?? {},
      addButton: true,
    },
  };

  ngOnInit(): void {
    this.activeIndex = 0;
  }

  tabAdd() {
    const index = this.activeIndex;
    const newItem = this.props.newItem(this.model?.[index]);
    this.add(undefined, newItem, {
      markAsDirty: true,
    });
    Promise.resolve().then(() => {
      this.cdr.detectChanges();
      this.activeIndex = (this.field.fieldGroup?.length || 1) - 1;
    });
  }

  tabRemove(index: number, event: Event) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.currentTarget || undefined,
      message: 'Naozaj chcete odstrániť záznam?',
      rejectButtonStyleClass: 'p-button-text',
      key: 'tab-remove-' + index,
      accept: () => {
        this.remove(index);
        if (this.activeIndex >= (this.field.fieldGroup?.length || 1) - 1) {
          this.activeIndex = Math.max(
            0,
            (this.field.fieldGroup?.length || 1) - 2,
          );
        }
      },
    });
  }

  activeIndex = 0;

  isValid(field: FormlyFieldConfig): boolean {
    if (field.key) {
      return field.formControl?.valid === true;
    }

    return field.fieldGroup
      ? field.fieldGroup.every((f) => this.isValid(f))
      : true;
  }
}
